import {
  publicCampaignApiGet,
  publicCampaignApiPost,
} from '@/lib/api/public-campaign-api-client';
import type {
  OptInState,
  PublicConsentData,
  PublicConsentEnvelope,
  UnsubscribeState,
} from '../types/public-consent.types';

const dataFrom = <TState extends string>(
  response: PublicConsentEnvelope<TState>,
  fallback: string
): PublicConsentData<TState> => {
  if (!response?.success || !response.data) {
    throw new Error(response?.message || response?.errors?.join(', ') || fallback);
  }
  return response.data;
};

export const getOptInConfirmation = async (
  token: string
): Promise<PublicConsentData<OptInState>> => {
  const response = await publicCampaignApiGet<PublicConsentEnvelope<OptInState>>(
    '/v1/optin/confirm',
    { token }
  );
  return dataFrom(response, 'Unable to load this confirmation link.');
};

export const confirmOptIn = async (token: string): Promise<PublicConsentData<OptInState>> => {
  const response = await publicCampaignApiPost<PublicConsentEnvelope<OptInState>>(
    '/v1/optin/confirm',
    { token }
  );
  return dataFrom(response, 'Unable to confirm your subscription.');
};

export const getUnsubscribe = async (
  token: string
): Promise<PublicConsentData<UnsubscribeState>> => {
  const response = await publicCampaignApiGet<PublicConsentEnvelope<UnsubscribeState>>(
    '/v1/unsubscribe',
    { token }
  );
  return dataFrom(response, 'Unable to load this unsubscribe link.');
};

export const unsubscribe = async (token: string): Promise<PublicConsentData<UnsubscribeState>> => {
  const response = await publicCampaignApiPost<PublicConsentEnvelope<UnsubscribeState>>(
    '/v1/unsubscribe',
    { token }
  );
  return dataFrom(response, 'Unable to unsubscribe. Please try again.');
};

export type OptInResendRequest = {
  email: string;
  token: string;
};

export type OptInResendResponse = {
  success: boolean;
  message: string;
  data: null;
  errors: string[];
};

export const requestOptInResend = async (
  input: OptInResendRequest
): Promise<OptInResendResponse> => {
  return publicCampaignApiPost<OptInResendResponse>('/optin/resend-request', input);
};

